import MarkdownInput from "ra-input-markdown"
import React, { FC } from "react"
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  FormDataConsumer,
  List,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
} from "react-admin"
import { JsonField, JsonInput } from "react-admin-json-view"

import MediaWithUpload from "./components/MediaWithUpload"
import { ProductStatus, ProductType } from "./generated/graphql"
import { EditToolbar, SearchFilter } from "./utils"

const productStatusChoices = [
  ...Object.keys(ProductStatus).map((k) => ({
    id: k,
    name: k,
  })),
]

const productTypeChoices = [
  ...Object.keys(ProductType).map((k) => ({
    id: k,
    name: k,
  })),
]

export const ProductCreate: FC<any> = (props) => (
  <Create {...props}>
    <SimpleForm>
      <TextInput source="name" />
      <SelectInput choices={productTypeChoices} source="productType" />
      <TextInput source="description" />
      <NumberInput source="order" />
      <MarkdownInput source="additionalInfo" />
      <TextInput source="code" />
      <NumberInput source="price" />
      <NumberInput label="VAT (percentage)" source="vat" />
      <NumberInput source="quantity" />
      <NumberInput label="Limit per user" source="limit" />
      <ReferenceInput reference="Community" source="community">
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <BooleanInput source="showBeforeStartDate" />
      <DateTimeInput source="startDate" />
      <DateTimeInput source="endDate" />
    </SimpleForm>
  </Create>
)

export const ProductEdit: FC<any> = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm toolbar={<EditToolbar />}>
        <TextField source="id" />
        <TextInput source="name" />
        <SelectInput choices={productTypeChoices} source="productType" />
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              <>
                {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                  formData.productType === ProductType.Bundle && (
                    <ReferenceArrayInput
                      label="Bundle Products"
                      reference="Product"
                      required
                      source="bundleProducts"
                      {...rest}
                    >
                      <AutocompleteArrayInput optionText="code" />
                    </ReferenceArrayInput>
                  )
                }
              </>
            )
          }}
        </FormDataConsumer>
        <NumberInput source="order" />
        <TextInput source="description" />
        <MarkdownInput source="additionalInfo" />
        <TextInput source="code" />
        <TextInput source="externalBuyLink" />
        <TextInput source="pickupInfo" />
        <NumberInput source="price" />
        <NumberInput label="VAT (percentage)" source="vat" />
        <NumberInput source="quantity" />
        <ReferenceArrayInput
          label="Shared Quantity Products"
          reference="Product"
          source="sharedQuantityProducts"
        >
          <AutocompleteArrayInput optionText="code" />
        </ReferenceArrayInput>

        <NumberInput label="Limit per user" source="limit" />
        <NumberField source="totalSold" />
        <TextInput source="mediaUrl" />
        <MediaWithUpload
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          filename={props["id"] as string}
          label="Image"
          path="product/image"
        />
        <ReferenceInput reference="Community" source="community">
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput reference="Event" source="event">
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <SelectInput choices={productStatusChoices} source="publishingStatus" />
        <DateTimeInput source="soldoutAfter" />
        <BooleanInput source="showBeforeStartDate" />
        <DateTimeInput source="startDate" />
        <DateTimeInput source="endDate" />
        <TextInput source="requiredAccessCode" />
        <TextInput allowNull source="customReceiptAlias" />
        <JsonInput
          jsonString={false}
          reactJsonOptions={{
            collapsed: false,
            displayDataTypes: false,
            enableClipboard: false,
            name: null,
          }}
          source="fees"
        />
        <ReferenceField label="Community Payment Fees" reference="Community" source="community">
          {/* <TextField source="paymentFees" /> */}
          <JsonField
            jsonString={false}
            reactJsonOptions={{
              collapsed: false,
              displayDataTypes: false,
              enableClipboard: false,
              name: null,
            }}
            source="paymentFees"
          />
        </ReferenceField>
        <JsonInput
          jsonString={false}
          reactJsonOptions={{
            collapsed: true,
            displayDataTypes: false,
            enableClipboard: false,
            name: null,
          }}
          source="additionalConfig"
        />
      </SimpleForm>
    </Edit>
  )
}

export const ProductList: FC<any> = (props) => (
  <List
    {...props}
    filter={{
      publishingStatus: {
        in: ["Draft", "CompleteAvailable", "CompleteUnavailable", "Hidden"],
      },
    }}
    filters={SearchFilter(props)}
  >
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <SelectField choices={productTypeChoices} source="productType" />
      <TextField source="publishingStatus" />
      <TextField source="description" />
      <TextField source="code" />
      <TextField source="payees" />
      <NumberField source="price" />
      <NumberField source="quantity" />
      <NumberField source="totalSold" />
      <TextField source="status" />
      <ReferenceField reference="Community" source="community">
        <ChipField source="name" />
      </ReferenceField>
      <DateField source="startDate" />
      <DateField source="endDate" />
    </Datagrid>
  </List>
)
